import { ReactNode, useState } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import {
  LayoutDashboard,
  Users,
  Award,
  FileText,
  HelpCircle,
  Settings,
  LogOut,
  ChevronRight,
  Mail,
  Layout,
  Menu,
  X,
  BookOpen,
  MessageSquare,
  Brain,
  Camera,
  Key,
  BarChart3
} from 'lucide-react'
import { TessanLogo } from '@/components/ui/TessanLogo'

interface AdminLayoutProps {
  children: ReactNode
  title: string
  breadcrumbs?: Array<{ label: string; href?: string }>
}

const NAV_ITEMS = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/users', label: 'Utilisateurs', icon: Users },
  { href: '/admin/posts', label: 'Publications', icon: MessageSquare },
  { href: '/admin/badges', label: 'Badges', icon: Award },
  { href: '/admin/weekly_quizzes', label: 'Quiz Hebdo', icon: Brain },
  { href: '/admin/surveys', label: 'Sondages', icon: BarChart3 },
  { href: '/admin/photo_contests', label: 'Concours Photo', icon: Camera },
  { href: '/admin/categories', label: 'Catégories', icon: FileText },
  { href: '/admin/articles', label: 'Articles', icon: BookOpen },
  { href: '/admin/faq_items', label: 'FAQ', icon: HelpCircle },
  { href: '/admin/invitations', label: 'Invitations', icon: Mail },
  { href: '/admin/communities', label: 'Communautés', icon: Settings },
  { href: '/admin/page_contents', label: 'Contenus', icon: Layout },
  { href: '/admin/api_keys', label: 'Clés API', icon: Key },
]

export function AdminLayout({ children, title, breadcrumbs }: AdminLayoutProps) {
  const { url } = usePage()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const isActive = (href: string) => {
    if (href === '/admin') return url === '/admin'
    return url.startsWith(href)
  }

  return (
    <>
      <Head title={`Admin - ${title}`} />
      {/* Root container - fixed height, no scroll on body */}
      <div className="h-screen overflow-hidden bg-gray-100 flex">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - fixed height, does not scroll with content */}
        <aside
          className={`
            fixed lg:relative top-0 left-0 z-50 h-screen w-64 shrink-0
            bg-tessan-primary text-white
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            flex flex-col
          `}
        >
          {/* Logo header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-white/10 shrink-0">
            <Link href="/" className="flex items-center gap-2 text-white">
              <TessanLogo height={24} />
              <span className="text-sm font-medium text-white/80">Admin</span>
            </Link>

            {/* Close button - mobile only */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 text-white/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation - scrollable if too many items */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-2">
              {NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                      ${isActive(item.href)
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer - Back to site */}
          <div className="border-t border-white/10 p-2 shrink-0">
            <Link
              href="/"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              <LogOut size={20} />
              <span>Retour au site</span>
            </Link>
          </div>
        </aside>

        {/* Main content area - this is the only scrollable area */}
        <div className="flex-1 flex flex-col h-screen overflow-hidden lg:ml-0">
          {/* Mobile header - sticky */}
          <header className="lg:hidden bg-white shadow-sm shrink-0 z-30">
            <div className="flex items-center justify-between h-14 px-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 text-gray-600 hover:text-tessan-primary transition-colors"
              >
                <Menu size={24} />
              </button>
              <span className="font-medium text-gray-900">{title}</span>
              <div className="w-10" />
            </div>
          </header>

          {/* Scrollable content wrapper */}
          <div className="flex-1 overflow-y-auto">
            {/* Page header with breadcrumbs - desktop */}
            <div className="bg-white shadow-sm px-6 py-4 hidden lg:block">
              {breadcrumbs && breadcrumbs.length > 0 && (
                <nav className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <Link href="/admin" className="hover:text-tessan-primary transition-colors">
                    Admin
                  </Link>
                  {breadcrumbs.map((crumb, i) => (
                    <span key={i} className="flex items-center gap-2">
                      <ChevronRight size={14} />
                      {crumb.href ? (
                        <Link href={crumb.href} className="hover:text-tessan-primary transition-colors">
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="text-gray-900">{crumb.label}</span>
                      )}
                    </span>
                  ))}
                </nav>
              )}
              <h1 className="text-2xl font-display font-bold text-gray-900">{title}</h1>
            </div>

            {/* Mobile breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
              <div className="lg:hidden bg-gray-50 px-4 py-2 border-b">
                <nav className="flex items-center gap-2 text-xs text-gray-500 overflow-x-auto">
                  <Link href="/admin" className="hover:text-tessan-primary shrink-0">
                    Admin
                  </Link>
                  {breadcrumbs.map((crumb, i) => (
                    <span key={i} className="flex items-center gap-1 shrink-0">
                      <ChevronRight size={12} />
                      {crumb.href ? (
                        <Link href={crumb.href} className="hover:text-tessan-primary">
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="text-gray-700">{crumb.label}</span>
                      )}
                    </span>
                  ))}
                </nav>
              </div>
            )}

            {/* Content */}
            <main className="p-4 lg:p-6">{children}</main>
          </div>
        </div>
      </div>
    </>
  )
}
